import { ConstantItems, getDefaultConfig } from "../../constants";
import * as _ from "lodash";
import { FeeType, PortfolioFee } from "./models/fee.models";

export class FeesRepo {
  /** @ngInject */
  constructor(
    private $http: angular.IHttpService,
    private config: ConstantItems
  ) {}

  getManagementFeesSummary(fromDate, toDate, currency) {
    let queryParams = {
      toDate: toDate,
      fromDate: fromDate,
      currency: currency,
    };

    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.feesUrl}/summary`, config);
  }

  getClientManagementFees = (clientId, feeDate, currency) => {
    let queryParams = {
      feeDate: feeDate,
      currency: currency,
    };

    let config = getDefaultConfig();

    config.params = queryParams;
    return this.$http.get(
      `${this.config.feesUrl}/summary/clients/${clientId}`,
      config
    );
  };

  getFeeTypes = (isActive: boolean = true) => {
    let queryParams = {
      isActive: isActive,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(`${this.config.feesUrl}/feetypes`, config);
  };

  saveFeeType = (feeType: FeeType) => {
    return this.$http.post(`${this.config.feesUrl}/feetypes`, feeType);
  };

  editFeeType = (feeType: FeeType) => {
    return this.$http.put(`${this.config.feesUrl}/feetypes`, feeType);
  };

  getClientFees = (clientNumber: string) => {
    return this.$http.get(`${this.config.feesUrl}/clients/${clientNumber}`);
  };

  savePortfolioFees = (portfolioFee: PortfolioFee) => {
    return this.$http.post(`${this.config.feesUrl}/clients`, portfolioFee);
  };

  editClientFee = (portfolioFee: PortfolioFee) => {
    return this.$http.put(`${this.config.feesUrl}/clients`, portfolioFee);
  };

  getPortfolioFeeExclusion = (portfolioId: number) => {
    return this.$http.get(
      `${this.config.feesUrl}/portfolios/${portfolioId}/exclusions`
    );
  };

  savePortfolioFeeExclusion = (portfolioFeeExclusion) => {
    return this.$http.post(
      `${this.config.feesUrl}/portfolios/exclusions`,
      portfolioFeeExclusion
    );
  };

  editPortfolioFeeExclusion = (portfolioFeeExclusion) => {
    return this.$http.put(
      `${this.config.feesUrl}/portfolios/exclusions`,
      portfolioFeeExclusion
    );
  };

  getManagementFeeCashAccounts = () => {
    return this.$http.get(`${this.config.feesUrl}/cashaccounts`);
  };

  getClientFeeCashAccounts = (clientId) => {
    return this.$http.get(
      `${this.config.feesUrl}/clients/${clientId}/cashaccounts`
    );
  };

  addEditPortfolioFeeCashAccount = (accountDetails) => {
    return this.$http.post(
      `${this.config.feesUrl}/portfolios/cashaccounts`,
      accountDetails
    );
  };

  getManagementFeeParameters = (
    fromDate,
    toDate,
    status,
    currency,
    calculationType
  ) => {
    let queryParams = {
      fromDate: fromDate,
      toDate: toDate,
      status: status,
      currency: currency,
      calculationType: calculationType,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.feesUrl}/instructions/parameters`,
      config
    );
  };

  saveTempRunParameters = (tempRunParameters) => {
    return this.$http.post(
      `${this.config.feesUrl}/instructions/parameters`,
      tempRunParameters
    );
  };

  getFinalRunParameters = (fromDate, toDate, calculationType) => {
    let queryParams = {
      fromDate: fromDate,
      toDate: toDate,
      calculationType: calculationType,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.feesUrl}/instructions/parameters/finalrun`,
      config
    );
  };

  declineFeeInstruction = (feeInstructionId) => {
    return this.$http.delete(
      `${this.config.feesUrl}/instructions/parameters/${feeInstructionId}`
    );
  };

  generateInstructions = (instructions, valueDate) => {
    let queryParams = {
      valueDate: valueDate,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.post(
      `${this.config.feesUrl}/instructions/generate`,
      instructions,
      config
    );
  };

  getFeeInstructionSheets = (fromDate, toDate, feeCalculationType) => {
    let queryParams = {
      fromDate: fromDate,
      toDate: toDate,
      feeCalculationType: feeCalculationType,
    };
    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(`${this.config.feesUrl}/instructions/sheets`, config);
  };

  downloadFeeInstruction = (sheetId) => {
    return this.$http.get(
      `${this.config.feesUrl}/instructions/sheets/${sheetId}/download`
    );
  };

  getMidRate = (calculationDate) => {
    let queryParams = {
      calculationDate: calculationDate,
    };

    let config = getDefaultConfig();

    config.params = queryParams;

    return this.$http.get(`${this.config.feesUrl}/instructions/rates`, config);
  };

  generateAdhocFeeInstructionSheet = (
    fromDate,
    toDate,
    calculationType,
    includeInstructed: boolean = false
  ) => {
    let queryParams = {
      fromDate: fromDate,
      toDate: toDate,
      calculationType: calculationType,
      includeInstructed: includeInstructed,
    };

    let config = getDefaultConfig();

    config.params = queryParams;

    return this.$http.get(
      `${this.config.feesUrl}/instructions/generate/parameters/finalrun`,
      config
    );
  };

  generateAdhocTempRunSheet = (fromDate, toDate, calculationType) => {
    let queryParams = {
      fromDate: fromDate,
      toDate: toDate,
      calculationType: calculationType,
    };

    let config = getDefaultConfig();

    config.params = queryParams;

    return this.$http.get(
      `${this.config.feesUrl}/instructions/generate/parameters/temprun`,
      config
    );
  };

  getManagementFeesByCustodian = (fromDate, toDate, currency) => {
    let queryParams = {
      fromDate: fromDate,
      toDate: toDate,
      currency: currency,
    };

    let config = getDefaultConfig();

    config.params = queryParams;

    return this.$http.get(
      `${this.config.feesUrl}/summary/fees/custodians`,
      config
    );
  };

  getPortfolioFeesByLimit = (
    date,
    limit = 10,
    displayTop = true,
    displayCurrency,
    feeCalculationType,
    sortByNav = false
  ) => {
    let queryParams = {
      date: date,
      displayTop: displayTop,
      displayCurrency: displayCurrency,
      limit: limit,
      feeCalculationType: feeCalculationType,
      sortByNav: sortByNav,
    };

    let config = getDefaultConfig();

    config.params = queryParams;

    return this.$http.get(
      `${this.config.feesUrl}/summary/fees/portfolios`,
      config
    );
  };

  getClientFeesByLimit = (
    date,
    limit = 10,
    displayTop = true,
    displayCurrency,
    feeCalculationType,
    sortByNav = false
  ) => {
    let queryParams = {
      date: date,
      displayTop: displayTop,
      displayCurrency: displayCurrency,
      limit: limit,
      feeCalculationType: feeCalculationType,
      sortByNav: sortByNav,
    };

    let config = getDefaultConfig();

    config.params = queryParams;

    return this.$http.get(
      `${this.config.feesUrl}/summary/fees/clients`,
      config
    );
  };

  getFeeTypesFeesByLimit = (
    date,
    limit = 10,
    displayTop = true,
    displayCurrency,
    feeCalculationType,
    sortByNav = false
  ) => {
    let queryParams = {
      date: date,
      displayTop: displayTop,
      displayCurrency: displayCurrency,
      limit: limit,
      feeCalculationType: feeCalculationType,
      sortByNav: sortByNav,
    };

    let config = getDefaultConfig();

    config.params = queryParams;

    return this.$http.get(
      `${this.config.feesUrl}/summary/fees/feetypes`,
      config
    );
  };

  getAuditExcel = (fromDate, toDate, calculationType) => {
    let queryParams = {
      fromDate: fromDate,
      toDate: toDate,
      calculationType: calculationType,
    };

    let config = getDefaultConfig();

    config.params = queryParams;

    return this.$http.get(`${this.config.feesUrl}/summary/fees/audit`, config);
  };

  recalculateFees = (clientFees) => {
    return this.$http.post(
      `${this.config.feesUrl}/clients/recalculate`,
      JSON.stringify(clientFees)
    );
  };
}



// WEBPACK FOOTER //
// ./src/app/fees/fees.repo.ts